import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker, dateToCalendarDate, calendarDateToDate } from '@/components/ui/date-picker';
import FileField from './FileField';
import NumberInput from '@/components/ui/NumberInput';
import SelectField from './SelectField';

const FieldControl = ({
  className,
  name,
  type,
  handleChange,
  value,
  readOnly,
  path,
  disabled,
  min,
  max,
  decimals,
  options,
  inModal
}) => {
  switch (type) {
    case 'text':
      return <Input
        className={cn(className, "text-xs sm:text-sm md:text-base")}
        onChange={handleChange}
        value={value || ''}
        readOnly={readOnly}
        disabled={disabled}
      />;
    case 'toggle':
      return <input
        type="checkbox"
        className={cn("toggle", className)}
        onChange={handleChange}
        defaultChecked={value}
        readOnly={readOnly}
        disabled={disabled}
      />;
    case 'date':
      return <DatePicker
        className={className}
        value={dateToCalendarDate(value)}
        onChange={handleChange}
        disabled={readOnly || disabled}
        inModal={inModal}
      />;
    case 'file':
      return <FileField
        className={className}
        name={name}
        onChange={handleChange}
        value={value}
        path={path}
        readOnly={readOnly || disabled}
        onDeleteConfirm={handleChange}
      />;
    case 'currency':
      return <NumberInput
        className={cn(className, "text-xs sm:text-sm md:text-base")}
        currency={true}
        decimals={decimals || 2}
        min={min}
        max={max}
        onChange={handleChange}
        value={value || ''}
        disabled={disabled || readOnly}
        placeholder="Ingrese monto"
      />;
    case 'number':
      return <NumberInput
        className={cn(className, "text-xs sm:text-sm md:text-base")}
        currency={false}
        decimals={decimals || 2}
        min={min}
        max={max}
        onChange={handleChange}
        value={value || ''}
        disabled={disabled || readOnly}
        placeholder="Ingrese número"
      />;
    case 'select':
      return <SelectField
        options={options || []}
        value={value}
        onChange={handleChange}
        isReadOnly={readOnly || disabled}
      />;
    default:
      return <Input
        type={type}
        className={cn(className, "text-xs sm:text-sm md:text-base")}
        onChange={handleChange}
        value={value || ''}
        readOnly={readOnly}
        disabled={disabled}
      />;
  }
}







const FieldLabel = ({ label, input = false, onChange, stylesInput, placeholder = null }) => {

  if (!label && !input) return <></>;

  if (input) return <Input
    value={label}
    onChange={onChange}
    placeholder={placeholder}
    className="text-xs sm:text-sm md:text-base"
    {...stylesInput}
  />

  return <Label
    className="text-xs sm:text-sm md:text-base text-nowrap"
  >
    {label}
  </Label>
}






export default function Field({
  type,
  name,
  label,
  inline = false,
  value,
  onChange,
  readOnly = false,
  options = undefined,
  path = null,
  helpText = null,
  maxWidth = "100%",
  visible = true,
  disabled = false,
  labelWritable = false,
  labelPlaceHolder = null,
  helpComponent = null,
  labelOnChange = () => { },
  // Props específicas para NumberInput
  min = undefined,
  max = undefined,
  decimals = undefined,
  // Prop para DatePicker en modales
  inModal = false
}) {

  const waitingFile = type === 'file' && readOnly && !path;//Si no existe path y es archivo

  const styles = {
    rootContainer: {
      display: inline ? ['block', 'block', 'inline-flex'] : 'block',
      py: 2,
      width: '100%',
      maxWidth
    },
    labelContainer: {
      width: ['100%', '100%', '15vw', '15vw'],
      p: "2"
    },
    helpContainer: {
      display: [, , 'flex'],
      alignItems: 'baseline',
      width: ['100%', '100%', '40vw', '40vw'],
      p: "1",
    },
    inputLabel: {
      maxW: ['150px'],
      size: "xs",
      fontSize: "sm",
      borderRadius: "base"
    },
    helpText: {
      fontSize: "xs",
      maxW: "md",
      pl: [0, 0, 4, 4],
      pt: [2, 2, 0, 0]
    },
  }




  const handleChange = (e) => {
    switch (type) {
      case 'toggle':
        onChange && onChange({ name, value: e.target.checked });
        break;
      case 'date':
        // Para DatePicker, e es un CalendarDate que convertimos a Date
        const date = calendarDateToDate(e);
        onChange && onChange({ name, value: date });
        break;
      case 'file':
        onChange && e && onChange({ name: e.name, value: e.value });
        break;
      case 'currency':
      case 'number':
        // Para NumberInput, e es directamente el valor string
        onChange && onChange({ name, value: e });
        break;
      case 'select':
        // Para SelectField, e simula el evento target
        onChange && onChange({ name, value: e.target.value });
        break;
      default:
        onChange && onChange({ name, value: e.target.value });
        break;
    }
  };


  if (!visible) return <></>;
  return (
    <div className={cn(
      "py-2 w-full",
      inline ? "block md:flex md:items-start" : "block",
      maxWidth && `max-w-[${maxWidth}]`
    )}>

      {/* LABEL CONTAINER */}
      {(label || labelWritable) && (
        <div className="w-full md:w-36 lg:w-40 p-2 flex-shrink-0">
          <FieldLabel
            label={label}
            input={labelWritable}
            placeholder={labelPlaceHolder}
            onChange={labelOnChange}
          />
        </div>
      )}

      <div className="w-full md:flex-1 p-1 min-w-0">
        {!waitingFile && (
          <FieldControl
            name={name}
            type={type}
            handleChange={handleChange}
            value={value}
            readOnly={readOnly}
            options={options}
            path={path}
            disabled={disabled}
            min={min}
            max={max}
            decimals={decimals}
            inModal={inModal}
          />
        )}
        {waitingFile && <p className="p-2 text-sm">Esperando archivo...</p>}
      </div>

      {(helpText || helpComponent) && (
        <div className="w-full md:flex-1 p-1 md:flex items-baseline min-w-0">
          {helpComponent}
          {helpText && (
            <p className="text-xs max-w-md pl-0 md:pl-4 pt-2 md:pt-0">
              {helpText}
            </p>
          )}
        </div>
      )}
    </div>
  );
}